import { checkPrerequisites } from '@/services/analytics';
import { datadogRum } from '@datadog/browser-rum';

export const DEFAULT_CONNECTIVITY_INTERVAL_MS = 5000;
export const MAX_CONNECTIVITY_HISTORY_ENTRIES = 500;
export const CONNECTIVITY_LATENCY_THRESHOLD_MS = 500;
export const HEARTBEAT_TIMEOUT_MS = 4000;
export const CONNECTIVITY_ISSUE_REMINDER_EVERY = 12;
const HEARTBEAT_URL = '/ping.txt';
const CONNECTIVITY_ISSUE_TAG = 'network_connectivity_issue';

export type ConnectivityEntry = {
  ts: string;
  status: 'ok' | 'fail';
  latency: number;
};

const history: ConnectivityEntry[] = [];
let intervalId: number | null = null;
let visibilityChangeHandler: (() => void) | null = null;
let isHeartbeatInFlight = false;
let consecutiveIssueCount = 0;

function getErrorHistorySnapshot(): ConnectivityEntry[] {
  // Keep payload size bounded while still preserving recent state for investigations.
  return history.slice(-100);
}

function pushEntry(entry: ConnectivityEntry) {
  history.push(entry);

  if (history.length > MAX_CONNECTIVITY_HISTORY_ENTRIES) {
    history.shift();
  }
}

function reportConnectivityIssue(entry: ConnectivityEntry) {
  if (!checkPrerequisites()) {
    return;
  }

  datadogRum.addAction(CONNECTIVITY_ISSUE_TAG, {
    current: entry,
    history: getErrorHistorySnapshot(),
  });
}

async function runHeartbeatCheck() {
  if (isHeartbeatInFlight) {
    return;
  }

  isHeartbeatInFlight = true;
  const start = performance.now();
  const now = () => new Date().toISOString();
  const abortController = new AbortController();
  const timeoutId = window.setTimeout(() => {
    abortController.abort();
  }, HEARTBEAT_TIMEOUT_MS);

  let entry: ConnectivityEntry;

  try {
    const response = await fetch(HEARTBEAT_URL, {
      cache: 'no-store',
      signal: abortController.signal,
    });
    // Any response means network path is reachable (including non-2xx/3xx such as 404/301).
    void response;

    entry = {
      ts: now(),
      status: 'ok',
      latency: performance.now() - start,
    };
  } catch {
    entry = {
      ts: now(),
      status: 'fail',
      latency: performance.now() - start,
    };
  } finally {
    window.clearTimeout(timeoutId);
    isHeartbeatInFlight = false;
  }

  pushEntry(entry);

  const hasConnectivityIssue =
    entry.status === 'fail' || entry.latency > CONNECTIVITY_LATENCY_THRESHOLD_MS;

  if (!hasConnectivityIssue) {
    consecutiveIssueCount = 0;
    return;
  }

  consecutiveIssueCount += 1;
  const shouldReportIssue =
    consecutiveIssueCount === 1 || consecutiveIssueCount % CONNECTIVITY_ISSUE_REMINDER_EVERY === 0;

  if (shouldReportIssue) {
    reportConnectivityIssue(entry);
  }
}

export function startConnectivityMonitor(intervalMs = DEFAULT_CONNECTIVITY_INTERVAL_MS) {
  if (intervalId !== null) {
    return;
  }

  const runTick = () => {
    // Browsers throttle hidden tabs; skip checks to avoid false degraded/fail signals.
    if (document.hidden) {
      return;
    }

    void runHeartbeatCheck();
  };

  runTick();
  intervalId = window.setInterval(() => {
    runTick();
  }, intervalMs);

  visibilityChangeHandler = () => {
    if (!document.hidden) {
      runTick();
    }
  };

  document.addEventListener('visibilitychange', visibilityChangeHandler);
}

export function stopConnectivityMonitor() {
  if (intervalId === null) {
    return;
  }

  window.clearInterval(intervalId);
  intervalId = null;

  if (visibilityChangeHandler) {
    document.removeEventListener('visibilitychange', visibilityChangeHandler);
    visibilityChangeHandler = null;
  }
}

export function getConnectivityHistory(): ConnectivityEntry[] {
  return [...history];
}
